import React, { useState } from "react";
import { Modal, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import type { NormalizedRelease } from "@rista/core/whatsNewSection";
import { formatReleaseDate } from "@rista/core/whatsNewSection";
import { MODAL_ORIENTATIONS } from "@rista/ui/library/components/ModalOrientations";
import * as Components from "./components";
import useWhatsNewLogic from "./hooks";

const isWeb = Platform.OS === "web";

export const { getBadgeColors, renderBulletItems, renderBulletGroups, renderBadgeRows, renderAllBadgeItems } = Components;

export interface WhatsNewModalProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  onDismiss?: (hashKey?: string) => void;
  showVersionSelector?: boolean;
}

/* -------------------------------------------------------------------------- */
/* No Release Notes                                                           */
/* -------------------------------------------------------------------------- */

interface NoReleaseNotesModalProps {
  open?: boolean;
  isWeb: boolean;
  title: string|undefined;
  onClose: () => void;
  styles: any;
}

function NoReleaseNotesModal({ open, isWeb, title, onClose, styles }: NoReleaseNotesModalProps) {
  return (
    <Modal
      transparent
      visible={open}
      animationType={isWeb ? "fade" : "slide"}
      onRequestClose={onClose}
      supportedOrientations={MODAL_ORIENTATIONS}
    >
      <View style={styles.backdrop}>
        <View style={isWeb ? styles.webModalContainer : styles.mobileSheetContainer}>
          <View style={styles.modalHeaderRow}>
            <View style={styles.headerIconWrap}>
              <Text style={styles.headerIcon}>✦</Text>
            </View>
            <View style={styles.headerTextWrap}>
              <Text style={styles.modalTitle}>{title}</Text>
              <Text style={styles.metaText}>No release notes available</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>×</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.noReleaseContent}>
            <Text style={styles.noReleaseText}>There are currently no updates to show.</Text>
          </View>

          <View style={styles.modalFooter}>
            <TouchableOpacity onPress={onClose} style={styles.ctaButton}>
              <Text style={styles.ctaButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Whats New Header                                                           */
/* -------------------------------------------------------------------------- */

interface WhatsNewHeaderProps {
  isPopupHeader: boolean;
  title: string|undefined;
  selectedRelease: NormalizedRelease;
  totalUpdatesCount: number;
  onDismiss: () => void;
  styles: any;
}

function WhatsNewHeader({
  isPopupHeader,
  title,
  selectedRelease,
  totalUpdatesCount,
  onDismiss,
  styles,
}: WhatsNewHeaderProps) {
  /*
   * Center modal header.
   *
   * Used for the automatic Web What's New popup.
   */
  if (isPopupHeader) {
    return (
      <View style={styles.modalHeaderRow}>
        <View style={styles.headerIconWrap}>
          <Text style={styles.headerIcon}>✦</Text>
        </View>

        <View style={styles.headerTextWrap}>
          <Text style={styles.modalTitle}>{title}</Text>

          <Text style={styles.metaText}>
            Version {selectedRelease.version} · Released{" "}
            {formatReleaseDate(selectedRelease.releaseDate)} · {totalUpdatesCount} updates
          </Text>
        </View>

        <TouchableOpacity onPress={onDismiss} style={styles.closeButton}>
          <Text style={styles.closeButtonText}>×</Text>
        </TouchableOpacity>
      </View>
    );
  }

  /*
   * Drawer / mobile header.
   *
   * Used for:
   * - Web Profile → What's New
   * - Mobile What's New
   */
  return (
    <View style={styles.drawerHeader}>
      <View style={styles.drawerHeaderLeft}>
        <TouchableOpacity onPress={onDismiss} style={styles.backButton}>
          <Text style={styles.backIcon}>←</Text>
        </TouchableOpacity>
        <Text style={styles.screenTitle}>What&apos;s new</Text>
      </View>

      <TouchableOpacity onPress={onDismiss} style={styles.closeButton}>
        <Text style={styles.closeButtonText}>×</Text>
      </TouchableOpacity>
    </View>
  );
}

/* -------------------------------------------------------------------------- */
/* Unified Whats New Component                                                */
/* -------------------------------------------------------------------------- */

export function WhatsNewReleaseModal({ open = true, onClose, onDismiss, title, showVersionSelector }: WhatsNewModalProps) {
  /*
   * Dropdown open/close state is UI state,
   * so it remains inside the component.
   */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const defaultTitle = showVersionSelector ? "What’s new" : "What’s new in Rista";
  const resolvedTitle = title || defaultTitle;
  const closeHandler = onClose ?? onDismiss;

  /*
   * All release/version calculation is handled by the hook.
   */
  const {
    releases: releaseNotes,
    selectedRelease,
    normalizedIndex,
    totalUpdatesCount,
    handleVersionChange,
    handleOnDismiss
  } = useWhatsNewLogic({ onClose: closeHandler, autoOpen: false});

  /*
   * Center modal is ONLY the automatic Web popup.
   *
   * Mobile automatic popup remains the mobile sheet.
   *
   * Profile What's New on Web uses the drawer.
   */
  const isAutomaticPopup = !showVersionSelector;
  const isCenterModal = isWeb && isAutomaticPopup;

  if (!open) {
    return null;
  }

  if (!selectedRelease) {
    return (
      <NoReleaseNotesModal
        open={open}
        isWeb={isWeb}
        title={resolvedTitle}
        onClose={handleOnDismiss}
        styles={styles}
      />
    );
  }

  return (
    <Modal
      transparent
      visible={open}
      animationType={isCenterModal ? "fade" : "slide"}
      onRequestClose={handleOnDismiss}
      supportedOrientations={MODAL_ORIENTATIONS}
    >
      <View
        style={isCenterModal ? styles.backdrop : isWeb ? styles.drawerBackdrop : styles.backdrop}
      >
        <View
          style={
            isCenterModal
              ? styles.webModalContainer
              : isWeb
                ? styles.webDrawerContainer
                : styles.mobileSheetContainer
          }
        >
          {/* ---------------------------------------------------------------- */}
          {/* Mobile drag handle                                               */}
          {/* ---------------------------------------------------------------- */}

          {!isCenterModal && !isWeb && (
            <View style={styles.dragHandleWrap}>
              <View style={styles.dragHandle} />
            </View>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Header                                                           */}
          {/* ---------------------------------------------------------------- */}

          <WhatsNewHeader
            isPopupHeader={isAutomaticPopup}
            title={resolvedTitle}
            selectedRelease={selectedRelease}
            totalUpdatesCount={totalUpdatesCount}
            onDismiss={handleOnDismiss}
            styles={styles}
          />

          {/* ---------------------------------------------------------------- */}
          {/* Version selector + release meta                                  */}
          {/* ---------------------------------------------------------------- */}

          {!isAutomaticPopup && (
            <View style={styles.drawerFilterSection}>
              {showVersionSelector && (
                <>
                  <TouchableOpacity
                    activeOpacity={0.9}
                    onPress={() => setIsDropdownOpen((value) => !value)}
                    style={styles.dropdownButton}
                  >
                    <Text style={styles.dropdownText}>
                      Version {selectedRelease.version} ·{" "}
                      {formatReleaseDate(selectedRelease.releaseDate)}
                    </Text>

                    <Text style={styles.dropdownArrow}>{isDropdownOpen ? "▴" : "▾"}</Text>
                  </TouchableOpacity>

                  {isDropdownOpen && releaseNotes.length > 1 && (
                    <View style={styles.dropdownMenu}>
                      {releaseNotes.map((option: NormalizedRelease, index: number) => {
                        const isSelected = index === normalizedIndex;

                        return (
                          <TouchableOpacity
                            key={option.version || index}
                            activeOpacity={0.8}
                            onPress={() => {
                              handleVersionChange(index);
                              setIsDropdownOpen(false);
                            }}
                            style={[
                              styles.dropdownOption,
                              isSelected && styles.dropdownOptionActive,
                            ]}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                isSelected && styles.dropdownOptionTextActive,
                              ]}
                            >
                              Version {option.version} · {formatReleaseDate(option.releaseDate)}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                </>
              )}

              <Text style={styles.drawerMetaText}>
                Released {formatReleaseDate(selectedRelease.releaseDate)} · {totalUpdatesCount}{" "}
                updates
              </Text>
            </View>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Content                                                          */}
          {/* ---------------------------------------------------------------- */}

          <ScrollView
            style={isCenterModal ? styles.modalScrollArea : styles.drawerScrollArea}
            contentContainerStyle={styles.scrollContent}
          >
            {isCenterModal
              ? renderBulletGroups(selectedRelease, styles)
              : renderAllBadgeItems(selectedRelease, styles, getBadgeColors)}
          </ScrollView>

          {/* ---------------------------------------------------------------- */}
          {/* Automatic popup footer                                          */}
          {/* ---------------------------------------------------------------- */}

          {!showVersionSelector && (
            <View style={styles.modalFooter}>
              <TouchableOpacity onPress={handleOnDismiss} style={styles.ctaButton}>
                <Text style={styles.ctaButtonText}>Got it, thanks</Text>
              </TouchableOpacity>

              <Text style={styles.footerNote}>
                You&apos;ll find the full list anytime under What&apos;s New in the profile menu.
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Named exports                                                              */
/* -------------------------------------------------------------------------- */

export default WhatsNewReleaseModal;

/* -------------------------------------------------------------------------- */
/* Styles                                                                     */
/* -------------------------------------------------------------------------- */

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.4)",
    justifyContent: isWeb ? "center" : "flex-end",
    alignItems: "center",
    padding: isWeb ? 16 : 0,
  },

  drawerBackdrop: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.4)",
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },

  webModalContainer: {
    width: "100%",
    maxWidth: 480,
    maxHeight: "90%",
    backgroundColor: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    alignSelf: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.16,
    shadowRadius: 40,
    elevation: 24,
  },

  webDrawerContainer: {
    width: "100%",
    maxWidth: 540,
    height: "100%",
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: -4, height: 0 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 20,
  },

  mobileSheetContainer: {
    width: "100%",
    height: "70%",
    backgroundColor: "#fff",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: "hidden",
  },

  dragHandleWrap: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 10,
    paddingBottom: 4,
    backgroundColor: "#fff",
  },

  dragHandle: {
    width: 36,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: "#d1d5db",
  },

  /* Header */

  modalHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },

  drawerHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },

  drawerHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },

  backButton: {
    padding: 4,
    marginRight: 6,
  },

  backIcon: {
    fontSize: 20,
    color: "#4b5563",
  },

  screenTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
  },
  headerIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 8,
    backgroundColor: "#eff6ff",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  headerIcon: {
    fontSize: 18,
    color: "#3b82f6",
    fontWeight: "bold",
  },
  headerTextWrap: {
    flex: 1,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 2,
  },
  metaText: {
    fontSize: 12,
    color: "#9ca3af",
  },

  drawerMetaText: {
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 8,
  },
  closeButton: {
    padding: 4,
  },
  closeButtonText: {
    fontSize: 22,
    color: "#9ca3af",
    lineHeight: 22,
  },

  /* Version selector */

  drawerFilterSection: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
  },
  dropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    backgroundColor: "#fff",
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  dropdownText: {
    fontSize: 14,
    color: "#1f2937",
    fontWeight: "500",
  },
  dropdownArrow: {
    fontSize: 14,
    color: "#6b7280",
  },
  dropdownMenu: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  dropdownOption: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  dropdownOptionActive: {
    backgroundColor: "#eff6ff",
  },
  dropdownOptionText: {
    fontSize: 13,
    color: "#374151",
  },
  dropdownOptionTextActive: {
    color: "#2563eb",
    fontWeight: "600",
  },

  /* Content */

  modalScrollArea: {
    maxHeight: 420,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },

  drawerScrollArea: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 10,
  },

  scrollContent: {
    paddingBottom: 20,
  },
  groupedWrap: {
    gap: 16,
  },
  groupBlock: {
    marginBottom: 8,
  },
  groupTitle: {
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  newTitle: {
    color: "#2563eb",
  },

  improvedTitle: {
    color: "#059669",
  },

  fixedTitle: {
    color: "#d97706",
  },

  /* Bullet rows */

  bulletRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 10,
    paddingRight: 8,
  },

  bulletDot: {
    fontSize: 10,
    color: "#1f2937",
    marginRight: 8,
    marginTop: 4,
  },

  bulletTextWrap: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },

  bulletTitle: {
    fontWeight: "600",
    color: "#1f2937",
  },

  bulletDesc: {
    color: "#6b7280",
  },

  /* Badge rows */

  badgeRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },

  badgeRowContent: {
    flex: 1,
  },
  itemTitle: {
    fontSize: 14,
    color: "#1f2937",
    fontWeight: "600",
    lineHeight: 20,
  },
  itemDescription: {
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 18,
    marginTop: 3,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    alignSelf: "flex-start",
    marginTop: 2,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "600",
  },

  /* Footer */

  modalFooter: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    borderTopWidth: 1,
    borderTopColor: "#f3f4f6",
    backgroundColor: "#fafafc",
  },
  ctaButton: {
    backgroundColor: "#2563eb",
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  ctaButtonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
  footerNote: {
    fontSize: 11,
    color: "#9ca3af",
    textAlign: "center",
    marginTop: 10,
    lineHeight: 16,
  },

  /* No release notes */

  noReleaseContent: {
    padding: 20,
  },

  noReleaseText: {
    color: "#374151",
  },
});
